/*
 * Saved Brand Templates — fetches the org's saved brand templates via
 * DeckorateAPI's blob-sas endpoint, same two-step contract as Slide
 * Templates (see slideTemplatesApi.ts): POST blob-sas -> one manifest JSON
 * asset (blob_container/blob_path), GET + parse its `entries` into the
 * template list. Each entry's `pptxFile` resolves to a sibling blob.
 *
 * Manifest entry shape isn't pinned down, so normalisation below accepts
 * several likely field-name spellings and degrades gracefully rather than
 * crashing if the schema shifts.
 */

import { sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { insertTemplateSlides } from "../../shared/services/pptxTemplateSlideInsert";
import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { BrandColors } from "../../../types";
import { logError, scrubMessage } from "../../../../../telemetry";

const SAVED_BRAND_TEMPLATES_CONNECTOR = "deckorate";
const BLOB_SAS_PATH = "/v1/deckorate/content/blob-sas";

/* NOTE: client_id is hardcoded for now (same as Slide Templates) — replace
 * with the real Studio client id once available. */
const SAVED_BRAND_TEMPLATES_CLIENT_ID = 1;
const SAVED_BRAND_TEMPLATES_TRANSACTION_TYPE = "TemplateTransaction";
const SAVED_BRAND_TEMPLATES_TEMPLATE_TYPE = "Brand";

const MANIFEST_REQUEST_FAILED_MESSAGE =
  "Couldn't find the saved brand templates library — the blob-sas request didn't return a usable asset.";
const MANIFEST_DOWNLOAD_FAILED_MESSAGE = "Downloading the saved brand templates library failed.";
const MANIFEST_NOT_JSON_MESSAGE = "The saved brand templates response wasn't valid JSON.";

/** Thrown by downloadManifest/fetchSavedBrandTemplateBytes so callers can
 *  branch on the HTTP status (specifically 404) without parsing it back out
 *  of a message string. */
export class BlobFetchHttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "BlobFetchHttpError";
  }
}

export function logSavedBrandTemplatesError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "SavedBrandTemplatesError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: SAVED_BRAND_TEMPLATES_CONNECTOR, reason },
  );
}

interface BlobSasRequest {
  client_id: number;
  transaction_type: typeof SAVED_BRAND_TEMPLATES_TRANSACTION_TYPE;
  template_type: typeof SAVED_BRAND_TEMPLATES_TEMPLATE_TYPE;
}

interface RawBlobSasAsset {
  sas_url?: unknown;
  blob_container?: unknown;
  blob_path?: unknown;
}

interface RawBlobSasResponse {
  assets?: unknown;
}

interface ManifestSasAsset {
  readonly sasUrl: string;
  readonly blobPath: string;
  readonly blobContainer: string | null;
}

function firstManifestSasAsset(response: RawBlobSasResponse | null | undefined): ManifestSasAsset | null {
  const assets = response?.assets;
  if (!Array.isArray(assets) || assets.length === 0) return null;
  const asset = assets[0] as RawBlobSasAsset | undefined;
  if (typeof asset?.sas_url !== "string" || !asset.sas_url) return null;
  if (typeof asset.blob_path !== "string" || !asset.blob_path) return null;
  const blobContainer = typeof asset.blob_container === "string" && asset.blob_container ? asset.blob_container : null;
  return { sasUrl: asset.sas_url, blobPath: asset.blob_path, blobContainer };
}

/**
 * Rebuilds a fetchable file URL for `blobPath` against `sasUrl`'s origin +
 * query string. Unlike Slide Templates' SAS (scoped to the manifest's exact
 * folder, so swapping just the last path segment works — see
 * directorySasFileUrl), Brand's SAS can be scoped higher up (e.g. container
 * root); swapping only the last segment there drops subfolders and 404s. So
 * this rebuilds the path from `blob_container` + the FULL `blobPath`
 * instead, falling back to the last-segment swap only if `blob_container`
 * is missing from the response.
 */
function blobFileUrl(
  sasUrl: string,
  blobContainer: string | null,
  blobPath: string,
  onParseError: (message: string) => void,
): string {
  try {
    const url = new URL(sasUrl);
    if (blobContainer) {
      const encodedPath = blobPath.split("/").filter(Boolean).map(encodeURIComponent).join("/");
      url.pathname = `/${encodeURIComponent(blobContainer)}/${encodedPath}`;
      return url.toString();
    }

    const segments = blobPath.split("/");
    let fileName: string | undefined;
    for (let i = segments.length - 1; i >= 0; i--) {
      if (segments[i]) {
        fileName = segments[i];
        break;
      }
    }
    if (!fileName) return sasUrl;
    let pathEnd = url.pathname.length;
    while (pathEnd > 0 && url.pathname[pathEnd - 1] === "/") pathEnd--;
    url.pathname = `${url.pathname.slice(0, pathEnd)}/${encodeURIComponent(fileName)}`;
    return url.toString();
  } catch (error) {
    onParseError((error as TypeError).message);
    return sasUrl;
  }
}

/** Folder portion of a container-relative blob path — "" for a root-level
 *  blob. Used to resolve a sibling `pptxFile` before calling blobFileUrl. */
function blobDirName(blobPath: string): string {
  const idx = blobPath.lastIndexOf("/");
  return idx === -1 ? "" : blobPath.slice(0, idx);
}

export interface SavedBrandTemplate {
  readonly id: string;
  readonly name: string;
  /** "All brands" filter grouping. Null if the entry carries no brand field. */
  readonly brand: string | null;
  /** Row-preview swatches — from `themeColors` when present, else a flat
   *  colours/swatches/palette field. */
  readonly colors: readonly string[] | null;
  /** Full colour scheme from the manifest's `themeColors` object — enough to
   *  build a BrandSpec (buildBrandSpecFromThemeColors) with no .pptx
   *  download. Null falls back to `sourceFileUrlCandidates`. */
  readonly themeColors: Partial<BrandColors> | null;
  /** Best-guess fetchable URL for this entry's .pptx (first of
   *  `sourceFileUrlCandidates`); null when there's no filename to resolve. */
  readonly sourceFileUrl: string | null;
  /** Filename variants worth trying (see pptxFileCandidates) — uploaded
   *  files mix naming conventions, so `pptxFile` doesn't always match the
   *  real blob name. Empty iff `sourceFileUrl` is null. */
  readonly sourceFileUrlCandidates: readonly string[];
  /** Slide aspect ratio (e.g. "16:9", "4:3"), derived from the manifest
   *  entry's `slideDimensionsEmu`. Null when that field is missing — the
   *  manifest carries no font-name data at all, so unlike Format there's no
   *  "Title font"/"Body font" to show. */
  readonly aspectRatio: string | null;
  /** Standard slide-size name ("A4", "16:9", "Custom"...); null without a type or dimensions. */
  readonly slideSize?: string | null;
  /** SAS URL of the `.cache` file with slide thumbnails. Images stay out of the list: localStorage quota. */
  readonly thumbnailCacheUrl?: string | null;
  /** Every slide layout of the deck, in slide order. The first one is the
   *  card's thumbnail. */
  readonly slides?: readonly BrandTemplateSlide[];
}

/** The brand a template belongs to for the "All brands" filter: the manifest's
 *  brand field when the entry has one, otherwise the first word of its name
 *  ("Northwind Pitch" → "Northwind") — the library entries carry no brand field. */
export function brandOfTemplate(template: { readonly brand: string | null; readonly name: string }): string | null {
  if (template.brand) return template.brand;
  const first = template.name.trim().split(/\s+/)[0];
  return first || null;
}

/** One slide layout inside a brand template deck. */
export interface BrandTemplateSlide {
  /** 1-based position in the deck. */
  readonly index: number;
  readonly name: string;
  /** Key into the lookup buildBrandThumbnailLookup returns; null when the
   *  manifest has no thumbnail reference for this slide. */
  readonly thumbnailKey: string | null;
}

const BRAND_COLOR_KEYS = [
  "dk1", "dk2", "lt1", "lt2",
  "accent1", "accent2", "accent3", "accent4", "accent5", "accent6",
  "hlink", "folHlink",
] as const;

/** Parses a manifest entry's `themeColors`/`theme_colors` OBJECT (not the
 *  array shape `colors`/`swatches`/`palette` accept) into a partial
 *  BrandColors, e.g. `{ dk1: "000000", accent1: "2E74B5", ... }`. */
function parseThemeColors(raw: unknown): Partial<BrandColors> | null {
  if (typeof raw !== "object" || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const result: Partial<Record<(typeof BRAND_COLOR_KEYS)[number], string>> = {};
  for (const key of BRAND_COLOR_KEYS) {
    const value = obj[key];
    if (typeof value === "string" && value.trim()) result[key] = value.replace(/^#/, "");
  }
  return Object.keys(result).length > 0 ? result : null;
}

function themeColorSwatches(themeColors: Partial<BrandColors>): string[] {
  return [
    themeColors.accent1, themeColors.accent2, themeColors.accent3,
    themeColors.accent4, themeColors.accent5, themeColors.accent6,
  ].filter((c): c is string => typeof c === "string" && c.length > 0);
}

function firstString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
  }
  return null;
}

function firstStringArray(...candidates: unknown[]): readonly string[] | null {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.every((c) => typeof c === "string")) {
      const cleaned = (candidate as string[]).map((c) => c.replace(/^#/, "")).filter(Boolean);
      if (cleaned.length > 0) return cleaned;
    }
  }
  return null;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * Reduces a manifest entry's `slideDimensionsEmu: { cx, cy }` (the slide's
 * width/height in EMUs — PowerPoint's own page-size unit, present regardless
 * of which slide the entry describes) to a simplified ratio label like
 * "16:9" or "4:3". Returns null for missing/non-positive dimensions rather
 * than showing a nonsense ratio.
 */
function parseAspectRatio(raw: unknown): string | null {
  if (typeof raw !== "object" || raw === null) return null;
  const { cx, cy } = raw as { cx?: unknown; cy?: unknown };
  if (typeof cx !== "number" || typeof cy !== "number") return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || cx <= 0 || cy <= 0) return null;
  const divisor = gcd(Math.round(cx), Math.round(cy));
  if (divisor <= 0) return null;
  return `${Math.round(cx / divisor)}:${Math.round(cy / divisor)}`;
}

/** PowerPoint's slide-size presets in EMU. Letter/Overhead match Standard, so only an explicit type tells them apart. */
const SLIDE_SIZE_PRESETS_EMU: ReadonlyArray<readonly [label: string, cx: number, cy: number]> = [
  ["Standard", 9144000, 6858000],
  ["Ledger", 12192000, 9144000],
  ["A3", 12801600, 9601200],
  ["A4", 9906000, 6858000],
  ["B4", 10826496, 8119872],
  ["B5", 7168896, 5376672],
  ["35 mm", 10287000, 6858000],
  ["Banner", 7315200, 914400],
  ["16:9", 9144000, 5143500],
  ["16:10", 9144000, 5715000],
  ["Widescreen", 12192000, 6858000],
];

/** Fraction a dimension may differ from a preset and still count as it —
 *  absorbs the rounding PowerPoint applies to metric paper sizes. */
const SLIDE_SIZE_TOLERANCE = 0.005;

/** PowerPoint's `<p:sldSz type="...">` values (lower-cased, punctuation
 *  stripped) -> display name. Used when a manifest entry carries the type. */
const SLIDE_SIZE_TYPE_LABELS: Readonly<Record<string, string>> = {
  screen4x3: "Standard",
  standard: "Standard",
  letter: "Letter",
  ledger: "Ledger",
  a3: "A3",
  a4: "A4",
  b4iso: "B4",
  b4: "B4",
  b5iso: "B5",
  b5: "B5",
  "35mm": "35 mm",
  overhead: "Overhead",
  banner: "Banner",
  screen16x9: "16:9",
  screen16x10: "16:10",
  widescreen: "Widescreen",
  custom: "Custom",
};

function closeTo(actual: number, expected: number): boolean {
  return Math.abs(actual - expected) / expected <= SLIDE_SIZE_TOLERANCE;
}

/** Slide-size display name: an explicit type wins, else preset match by dimensions, else "Custom"; null if neither. */
export function resolveSlideSizeLabel(dimensionsEmu: unknown, sizeType?: unknown): string | null {
  if (typeof sizeType === "string") {
    const label = SLIDE_SIZE_TYPE_LABELS[sizeType.toLowerCase().replace(/[^a-z0-9]/g, "")];
    if (label) return label;
  }
  if (typeof dimensionsEmu !== "object" || dimensionsEmu === null) return null;
  const { cx, cy } = dimensionsEmu as { cx?: unknown; cy?: unknown };
  if (typeof cx !== "number" || typeof cy !== "number") return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || cx <= 0 || cy <= 0) return null;
  const preset = SLIDE_SIZE_PRESETS_EMU.find(([, presetCx, presetCy]) => closeTo(cx, presetCx) && closeTo(cy, presetCy));
  return preset ? preset[0] : "Custom";
}

/** Handles both `/`-separated blob paths and `\`-separated Windows-style paths. */
function pathBasename(path: string): string {
  const segments = path.split(/[/\\]/).filter(Boolean);
  return segments[segments.length - 1] ?? path;
}

/** `file basename#slide number`, lower-cased, so manifest and cache keys match across casing/separators. */
function brandThumbnailKey(file: string, slideNumber: number): string {
  return `${pathBasename(file).toLowerCase()}#${slideNumber}`;
}

function sniffImageMimeType(base64: string): string {
  if (base64.startsWith("/9j/")) return "image/jpeg";
  if (base64.startsWith("R0lGOD")) return "image/gif";
  if (base64.startsWith("Qk")) return "image/bmp";
  return "image/png";
}

/** Parses the `.cache` file into key -> `data:` URL. Never throws; bad input yields an empty map. */
export function buildBrandThumbnailLookup(raw: unknown): ReadonlyMap<string, string> {
  const lookup = new Map<string, string>();
  if (!Array.isArray(raw)) return lookup;
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const { FilePath, SlideNumber, Base64ImageData } = item as Record<string, unknown>;
    if (typeof FilePath !== "string" || !FilePath) continue;
    if (typeof SlideNumber !== "number" || !Number.isFinite(SlideNumber)) continue;
    if (typeof Base64ImageData !== "string" || !Base64ImageData) continue;
    const key = brandThumbnailKey(FilePath, SlideNumber);
    if (!lookup.has(key)) lookup.set(key, `data:${sniffImageMimeType(Base64ImageData)};base64,${Base64ImageData}`);
  }
  return lookup;
}

interface ThumbnailRef {
  readonly cacheFile: string;
  readonly key: string;
}

/** Reads an entry's `thumbnailRef: { cacheFile, filePath, slideNumber }`. */
function parseThumbnailRef(raw: unknown, fallbackSlideIndex: number): ThumbnailRef | null {
  if (typeof raw !== "object" || raw === null) return null;
  const ref = raw as Record<string, unknown>;
  const cacheFile = firstString(ref.cacheFile, ref.cache_file);
  const filePath = firstString(ref.filePath, ref.file_path);
  if (!cacheFile || !filePath) return null;
  const slideNumber = typeof ref.slideNumber === "number" ? ref.slideNumber : fallbackSlideIndex;
  return { cacheFile, key: brandThumbnailKey(filePath, slideNumber) };
}

/** "Northwind_Pitch.pptx" -> "Northwind Pitch". */
function humaniseFileName(raw: string): string {
  return raw
    .replace(/\.pptx$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

interface NormalisedManifestEntry extends SavedBrandTemplate {
  /** Not part of the public shape — used only to dedupe multiple
   *  per-slide entries for the same source file (see normaliseManifest). */
  readonly dedupeKey: string;
  /** This entry's own slide — the template's `slides` are every entry's slide
   *  for the same `pptxFile`. */
  readonly slide: BrandTemplateSlide;
}

function normaliseManifestEntry(
  raw: unknown,
  index: number,
  resolveSourceFileUrls: (pptxFile: string) => string[],
  resolveSiblingUrl: (file: string) => string | null,
): NormalisedManifestEntry | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;

  const pptxFile = firstString(entry.pptxFile, entry.pptx_file, entry.file, entry.fileName, entry.file_name);
  const name =
    firstString(entry.name, entry.templateName, entry.template_name, entry.label, entry.title) ??
    (pptxFile ? humaniseFileName(pptxFile) : null);
  if (!name) return null;

  const id = firstString(entry.id, entry.templateId, entry.template_id, entry.slug) ?? `saved-brand-template-${index}`;
  const brand = firstString(
    entry.brand, entry.brandName, entry.brand_name, entry.Brand, entry.BrandName,
    entry.clientName, entry.client_name, entry.client, entry.Client,
  );
  const themeColors = parseThemeColors(entry.themeColors ?? entry.theme_colors);
  const colors = themeColors ? themeColorSwatches(themeColors) : firstStringArray(entry.colors, entry.swatches, entry.palette);
  const sourceFileUrlCandidates = pptxFile ? resolveSourceFileUrls(pptxFile) : [];
  const sourceFileUrl = sourceFileUrlCandidates[0] ?? null;
  const dimensionsEmu = entry.slideDimensionsEmu ?? entry.slide_dimensions_emu;
  const aspectRatio = parseAspectRatio(dimensionsEmu);
  const slideSize = resolveSlideSizeLabel(dimensionsEmu, entry.slideSizeType ?? entry.slide_size_type);

  const slideIndex = typeof entry.slideIndex === "number" && entry.slideIndex > 0 ? entry.slideIndex : index + 1;
  const thumbnailRef = parseThumbnailRef(entry.thumbnailRef ?? entry.thumbnail_ref, slideIndex);
  const slide: BrandTemplateSlide = {
    index: slideIndex,
    name: firstString(entry.slideName, entry.slide_name) ?? `Slide ${slideIndex}`,
    thumbnailKey: thumbnailRef?.key ?? null,
  };
  const thumbnailCacheUrl = thumbnailRef ? resolveSiblingUrl(thumbnailRef.cacheFile) : null;

  return {
    id, name, brand, colors, themeColors, sourceFileUrl, sourceFileUrlCandidates, aspectRatio,
    slideSize, thumbnailCacheUrl, slides: [slide],
    dedupeKey: pptxFile ?? id, slide,
  };
}

/** The manifest has one entry per slide; entries sharing a `pptxFile` collapse into one template with `slides`. */
function normaliseManifest(
  raw: unknown,
  resolveSourceFileUrls: (pptxFile: string) => string[],
  resolveSiblingUrl: (file: string) => string | null,
): SavedBrandTemplate[] {
  let items: unknown[] | null = null;
  if (Array.isArray(raw)) {
    items = raw;
  } else if (Array.isArray((raw as { entries?: unknown })?.entries)) {
    items = (raw as { entries: unknown[] }).entries;
  } else if (Array.isArray((raw as { templates?: unknown })?.templates)) {
    items = (raw as { templates: unknown[] }).templates;
  }
  if (!items) {
    logSavedBrandTemplatesError(
      "saved-brand-templates-manifest-shape",
      "Manifest root was neither an array nor { entries: [] } / { templates: [] }",
    );
    return [];
  }

  const seenIds = new Set<string>();
  const uniqueId = (id: string): string => {
    if (!seenIds.has(id)) {
      seenIds.add(id);
      return id;
    }
    let suffix = 2;
    while (seenIds.has(`${id}-${suffix}`)) suffix += 1;
    const deduped = `${id}-${suffix}`;
    seenIds.add(deduped);
    return deduped;
  };

  // One group per source file, in first-seen order. The first entry names the
  // template; every entry contributes its slide to the deck's layouts.
  const groups = new Map<string, NormalisedManifestEntry[]>();
  items.forEach((item, index) => {
    const entry = normaliseManifestEntry(item, index, resolveSourceFileUrls, resolveSiblingUrl);
    if (!entry) return;
    const group = groups.get(entry.dedupeKey);
    if (group) group.push(entry);
    else groups.set(entry.dedupeKey, [entry]);
  });

  return [...groups.values()].map((group) => {
    const { dedupeKey: _dedupeKey, slide: _slide, ...first } = group[0]!;
    const slides = group.map((entry) => entry.slide).sort((a, b) => a.index - b.index);
    return {
      ...first,
      id: uniqueId(first.id),
      slideSize: group.find((entry) => entry.slideSize)?.slideSize ?? null,
      thumbnailCacheUrl: group.find((entry) => entry.thumbnailCacheUrl)?.thumbnailCacheUrl ?? null,
      slides,
    };
  });
}

async function requestManifestSasAsset(http: HttpClient, options: HttpRequestOptions): Promise<ManifestSasAsset> {
  const request: BlobSasRequest = {
    client_id: SAVED_BRAND_TEMPLATES_CLIENT_ID,
    transaction_type: SAVED_BRAND_TEMPLATES_TRANSACTION_TYPE,
    template_type: SAVED_BRAND_TEMPLATES_TEMPLATE_TYPE,
  };
  const response = await http.post<RawBlobSasResponse | null>(BLOB_SAS_PATH, request, options);
  const asset = firstManifestSasAsset(response);
  if (!asset) throw new Error(MANIFEST_REQUEST_FAILED_MESSAGE);
  return asset;
}

/**
 * KNOWN BACKEND MISMATCH (2026-08-28): blob-sas returns
 * `blob_path: ".../brands-signatures.json"` but the real blob is
 * `brands-signature.json` (singular). Produces the plural/singular filename
 * variant as a fallback, tried only after the exact path 404s. Remove once
 * the backend response matches the real blob name.
 */
function manifestPathCandidates(blobPath: string): string[] {
  const candidates = [blobPath];
  if (/signatures\.json$/i.test(blobPath)) {
    candidates.push(blobPath.replace(/signatures\.json$/i, "signature.json"));
  } else if (/signature\.json$/i.test(blobPath)) {
    candidates.push(blobPath.replace(/signature\.json$/i, "signatures.json"));
  }
  return candidates;
}

/**
 * KNOWN BACKEND INCONSISTENCY: uploaded filenames mix underscore/space
 * conventions (e.g. "WL_Template_Sep2025.pptx" vs "RRD Master 2025.pptx"),
 * and an entry's `pptxFile` doesn't always match the real blob's style.
 * Trying the swapped variant after the manifest's own value 404s covers
 * that — remove once the manifest and blob names agree.
 */
function pptxFileCandidates(pptxFile: string): string[] {
  const candidates = [pptxFile];
  if (pptxFile.includes("_")) candidates.push(pptxFile.replace(/_/g, " "));
  if (pptxFile.includes(" ")) candidates.push(pptxFile.replace(/ /g, "_"));
  return [...new Set(candidates)];
}

async function fetchBlobResponse(url: string, signal: AbortSignal | undefined, reason: string): Promise<Response> {
  const res = await fetch(url, {
    method: "GET",
    headers: sasVersionHeaders(url, logSavedBrandTemplatesError, reason),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      logSavedBrandTemplatesError("saved-brand-templates-blob-body-read", getErrorMessage(error, "Failed to read blob error body"));
      return "";
    });
    throw new BlobFetchHttpError(res.status, `Blob download failed (${res.status} ${errText})`);
  }
  return res;
}

async function downloadManifest(sasUrl: string, signal: AbortSignal | undefined): Promise<unknown> {
  const res = await fetchBlobResponse(sasUrl, signal, "saved-brand-templates-manifest-sas-version-parse");
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (error) {
    logSavedBrandTemplatesError("saved-brand-templates-manifest-parse", getErrorMessage(error, "Manifest JSON.parse failed"));
    throw new Error(MANIFEST_NOT_JSON_MESSAGE);
  }
}

/**
 * Fetches a saved template's raw .pptx bytes, trying each `candidateUrls`
 * entry in order and only continuing on a 404 (any other failure surfaces
 * immediately). Shared by useBrandExtractor and useSavedBrandTemplateSwatches
 * so the fallback logic lives in one place.
 */
export async function fetchSavedBrandTemplateBytes(candidateUrls: readonly string[], signal?: AbortSignal): Promise<ArrayBuffer> {
  let lastError: unknown;
  for (const url of candidateUrls) {
    try {
      const res = await fetchBlobResponse(url, signal, "saved-brand-templates-source-sas-version-parse");
      return await res.arrayBuffer(); // NOSONAR fallback URLs are tried one at a time until one works
    } catch (error) {
      lastError = error;
      if (!(error instanceof BlobFetchHttpError) || error.status !== 404) throw error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Saved template download failed — no source file found.");
}

/** Fetch + parse the saved brand templates manifest, normalised into a lenient, UI-ready shape. */
export async function fetchSavedBrandTemplates(http: HttpClient, options: HttpRequestOptions = {}): Promise<SavedBrandTemplate[]> {
  const asset = await requestManifestSasAsset(http, options);
  const candidates = manifestPathCandidates(asset.blobPath);

  let manifest: unknown;
  let lastError: unknown;
  let resolvedBlobPath = asset.blobPath;
  for (const candidatePath of candidates) {
    const manifestFileUrl = blobFileUrl(asset.sasUrl, asset.blobContainer, candidatePath, (message) =>
      logSavedBrandTemplatesError("saved-brand-templates-sas-rebuild", message),
    );
    try {
      manifest = await downloadManifest(manifestFileUrl, options.signal);
      resolvedBlobPath = candidatePath;
      if (candidatePath !== asset.blobPath) {
        logSavedBrandTemplatesError(
          "saved-brand-templates-manifest-filename-mismatch",
          `blob-sas returned "${asset.blobPath}" but only "${candidatePath}" exists — using the fallback filename.`,
        );
      }
      break;
    } catch (error) {
      lastError = error;
      // Only a "not found at this exact path" error is worth trying another
      // filename for — any other failure (auth, network, malformed JSON)
      // would fail identically on every candidate, so surface it immediately.
      if (!(error instanceof BlobFetchHttpError) || error.status !== 404) throw error;
    }
  }
  if (manifest === undefined) throw lastError instanceof Error ? lastError : new Error(MANIFEST_DOWNLOAD_FAILED_MESSAGE);

  const manifestDir = blobDirName(resolvedBlobPath);
  const resolveSourceFileUrls = (pptxFile: string): string[] =>
    pptxFileCandidates(pptxFile).map((candidateFile) => {
      const siblingBlobPath = manifestDir ? `${manifestDir}/${candidateFile}` : candidateFile;
      return blobFileUrl(asset.sasUrl, asset.blobContainer, siblingBlobPath, (message) =>
        logSavedBrandTemplatesError("saved-brand-templates-source-url-rebuild", message),
      );
    });
  const resolveSiblingUrl = (file: string): string =>
    blobFileUrl(asset.sasUrl, asset.blobContainer, manifestDir ? `${manifestDir}/${file}` : file, (message) =>
      logSavedBrandTemplatesError("saved-brand-templates-thumbnail-cache-url-rebuild", message),
    );
  return normaliseManifest(manifest, resolveSourceFileUrls, resolveSiblingUrl);
}

/* ── Inserting layouts into the deck ─────────────────────────────── */

export const NO_INSERTABLE_LAYOUTS_MESSAGE = "None of the selected layouts have insertable slide content yet.";
const SOURCE_DOWNLOAD_FAILED_MESSAGE = "Downloading this brand template's source file failed.";

/**
  * Inserts the 1-based `slideIndexes` of `template` after the current slide, in deck order (BRD 8.6).
  * Task pane only: PowerPoint.run() is unavailable in the dialog. Returns the number inserted.
  */
export async function insertBrandTemplateLayouts(
  template: SavedBrandTemplate,
  slideIndexes: readonly number[],
): Promise<number> {
  // One entry per picked layout, all sharing the template's one source file.
  const layouts = [...new Set(slideIndexes)]
    .sort((a, b) => a - b)
    .map((slideIndex) => ({
      id: String(slideIndex),
      insertable: true,
      sourceFileUrl: template.sourceFileUrl,
      slideIndex,
    }));

  return insertTemplateSlides(
    layouts,
    layouts.map((layout) => layout.id),
    { noInsertableSelection: NO_INSERTABLE_LAYOUTS_MESSAGE, sourceDownloadFailed: SOURCE_DOWNLOAD_FAILED_MESSAGE },
    { log: logSavedBrandTemplatesError, reasonPrefix: "brand-templates" },
    {
      insertAfterCurrent: true,
      // The manifest's filename and the blob's can differ (underscores vs
      // spaces) — try each candidate, moving on only after a 404.
      fetchSource: () => fetchSavedBrandTemplateBytes(template.sourceFileUrlCandidates),
    },
  );
}

/** The in-pane confirmation after a successful insert (BRD 8.6). */
export function brandLayoutsAddedMessage(count: number): string {
  return count === 1 ? "1 layout added after your current slide." : `${count} layouts added after your current slide.`;
}

/** Why a layout picked in the window can't be inserted any more. */
export const BRAND_TEMPLATE_REMOVED_COPY = {
  title: "That brand template is no longer available",
  message: "Your organisation updated the library, so nothing was inserted. Choose another one instead.",
} as const;
